/*
 * @providesModule PlayerLevelUpController
 *
 */

import _ from 'lodash';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import GlobalEventEmitter from 'GlobalEventEmitter';
import ModalNavigationController from 'ModalNavigationController';
import NavigationManager from 'NavigationManager';
import PlayerLevelsApiController, {
  ShortListResponse as LevelsResponse,
} from 'PlayerLevelsApiController';
import PlayerLevelsScreen from 'PlayerLevelsScreen';
import PlayerLevelUpModal from 'PlayerLevelUpModal';
import React from 'react';
import SKZPlayerLevel from 'SKZPlayerLevel';
import WelcomeBackNavigator from 'WelcomeBackNavigator';
import {_m} from 'INTL';

import SKZLayoutAnimations from 'SKZLayoutAnimations';
import {Platform} from 'react-native';

const MODAL_PROPS = {
  animationType: 'fade',
  transparent: true,
};

class PlayerLevelUpController {
  _gameLevel: SKZPlayerLevel;
  _skillzLevel: SKZPlayerLevel;
  _shouldShowSkillzLevelUpModal = false;
  _shouldShowLevelUpModalLater = false;
  _completion: () => {};

  showLevelUpModal(
    isWelcomeBackFlow: boolean,
    shouldShowOrientationOverlay: boolean,
    completion: () => void
  ) {
    if (isWelcomeBackFlow) {
      WelcomeBackNavigator.hideModal();
    }
    const modalDetails = this._getModalCopy();
    const levelToShow = this._levelToShow();
    const levelUpScene = (
      <PlayerLevelUpModal
        level={levelToShow}
        title={modalDetails.title}
        subtitle={modalDetails.subtitle}
        body={modalDetails.body}
        showLearnMore={this._shouldShowLearnMore()}
        onNext={() => {
          this._handleNextButton(isWelcomeBackFlow);
        }}
        onLearnMore={this._handleLearnMore.bind(this, levelToShow, isWelcomeBackFlow)}
        shouldPlayConfettiOnAndroid={this._shouldPlayConfettiOnAndroid()}
        shouldShowOrientationOverlay={shouldShowOrientationOverlay}
      />
    );

    SKZLayoutAnimations.easeInEaseOut();
    if (isWelcomeBackFlow) {
      WelcomeBackNavigator.showModal(
        levelUpScene,
        MODAL_PROPS,
        this._handleNextButton.bind(this, isWelcomeBackFlow)
      );
    } else {
      NavigationManager.pushComponentWithScene(levelUpScene, true, 'PlayerLevelUpModal');
    }
    if (levelToShow.canMarkAsSeen()) {
      PlayerLevelsApiController.markLevelAsSeen(levelToShow);
    }
    if (completion) {
      this._completion = completion;
    }
  }

  showLevelUpModalLater(completion: () => {}) {
    this._shouldShowLevelUpModalLater = true;
    if (completion) {
      this._completion = completion;
    }
  }

  fetchPlayerLevels() {
    this._getPlayerLevels((levelData: LevelsResponse): boolean => {
      this._formatPlayerLevels(levelData);
    });
  }

  checkForPlayerLevels(): Promise {
    return new Promise((resolve: (Object) => void, reject: () => void) => {
      PlayerLevelsApiController.getShortList(false)
        .then((response: Array<SKZPlayerLevel>) => {
          this._formatPlayerLevels(response);
          resolve();
        })
        .catch((error: unknown) => {
          console.warn('PlayerLevelUpController: could not get level data ', error);
          reject();
        });
    });
  }

  shouldShowLevelUpModal(): boolean {
    if (!ClientPropertyConstants.getIsPlayerLevelsEnabled()) {
      return false;
    }

    if (ClientPropertyConstants.getWelcomeBackV3Enabled()) {
      // Hide player level up modal as part of Welcome Back v3.0
      return false;
    }

    if (!this._skillzLevel || !this._gameLevel) {
      return false;
    }

    if (
      _.get(this._skillzLevel, 'current_level') == 1 &&
      _.get(this._gameLevel, 'current_level') == 1
    ) {
      return false;
    }

    return (
      _.get(this._skillzLevel, 'current_level') > _.get(this._skillzLevel, 'last_seen_level') ||
      _.get(this._gameLevel, 'current_level') > _.get(this._gameLevel, 'last_seen_level')
    );
  }

  _shouldPlayConfettiOnAndroid(): boolean {
    if (this._levelToShow().isSkillzLevel()) {
      return true;
    }

    if (this._shouldShowBothLevelUpModals() && !this._levelToShow().isSkillzLevel()) {
      return false;
    } else {
      return true;
    }
  }

  _shouldShowBothLevelUpModals(): boolean {
    return (
      _.get(this._skillzLevel, 'current_level') > _.get(this._skillzLevel, 'last_seen_level') &&
      _.get(this._gameLevel, 'current_level') > _.get(this._gameLevel, 'last_seen_level')
    );
  }

  _formatPlayerLevels(levelsData: LevelsResponse) {
    this._skillzLevel = levelsData.skillzLevel;
    this._gameLevel = levelsData.gameLevel;

    if (_.get(this._gameLevel, 'last_seen_level') == 0) {
      this._gameLevel.last_seen_level = 1;
    }

    if (_.get(this._skillzLevel, 'last_seen_level') == 0) {
      this._skillzLevel.last_seen_level = 1;
    }
  }

  _showLevelUpModalNow() {
    if (this._shouldShowLevelUpModalLater) {
      this._shouldShowLevelUpModalLater = false;
      this.showLevelUpModal(false, false);
    }
  }

  _shouldShowLearnMore(): boolean {
    return this._levelToShow().isSkillzLevel() && (this._isFTUE() || this._isGrandFathered());
  }

  _handleNextButton(isWelcomeBackFlow: boolean) {
    const levelToShow = this._levelToShow();
    if (levelToShow.canMarkAsSeen()) {
      levelToShow.last_seen_level = levelToShow.current_level;
    }

    if (levelToShow.isSkillzLevel()) {
      if (levelToShow.current_level > levelToShow.last_seen_level) {
        this._shouldShowSkillzLevelUpModal = true;
        this.showLevelUpModal(isWelcomeBackFlow, false);
      } else {
        this._shouldShowSkillzLevelUpModal = false;
        this._completion();
      }
    } else {
      if (_.get(this._skillzLevel, 'current_level') > _.get(this._skillzLevel, 'last_seen_level')) {
        this._shouldShowSkillzLevelUpModal = true;
        this.showLevelUpModal(isWelcomeBackFlow, false);
      } else {
        this._completion();
      }
    }
  }

  _handleLearnMore(levelToShow: SKZPlayerLevel, isWelcomeBackFlow: boolean) {
    CurrentUserStore.getCurrentUserId().then((userId: string) => {
      const playerLevelsScreen = (
        <PlayerLevelsScreen
          userId={userId}
          onBack={() => {
            NavigationManager.popToRoot();
          }}
        />
      );

      NavigationManager.pushComponentWithScene(playerLevelsScreen, true, 'PlayerLevelsScreen');

      if (isWelcomeBackFlow) {
        WelcomeBackNavigator.hideModal();
        GlobalEventEmitter.emit('toggleShouldRender', true);
        GlobalEventEmitter.emit('SetWelcomeBackVisibility', false);
      } else {
        ModalNavigationController.hideModal();
      }
    });
  }

  _levelToShow(): SKZPlayerLevel {
    if (this._shouldShowSkillzLevelUpModal) {
      return this._skillzLevel;
    }
    return _.get(this._gameLevel, 'current_level') > _.get(this._gameLevel, 'last_seen_level')
      ? this._gameLevel
      : this._skillzLevel;
  }

  _getPlayerLevels(completion: (levelData: Array<SKZPlayerLevel>) => void) {
    PlayerLevelsApiController.getShortList(false)
      .then((response: LevelsResponse) => {
        completion(response);
      })
      .catch((error: unknown) => {
        console.warn('PlayerLevelUpController: could not get level data ', error);
      });
  }

  _getModalCopy(): Object {
    const levelToShow = this._levelToShow();
    const modalCopy = {
      title: _m('Congratulations!'),
      subtitle: _m("You've Leveled Up!"),
      body: '',
    };

    if (this._isGrandFathered() || this._isFTUE()) {
      if (levelToShow.isSkillzLevel()) {
        modalCopy.title = _m('Introducing Skillz Levels');
        modalCopy.subtitle = _m("You're Level {0} on Skillz!", _.get(levelToShow, 'current_level'));
        modalCopy.body = _m(
          'Your Skillz Level is a combination of all your gameplay across the Skillz Network.'
        );
      } else {
        modalCopy.title = _m('Introducing Levels');
        modalCopy.subtitle = _m("You're Level {0}!", _.get(levelToShow, 'current_level'));
        if (this._isFTUE()) {
          modalCopy.body = _m(
            'Your level represents all your hard work and gameplay in {0}',
            _.get(levelToShow, 'game_name')
          );
        } else {
          modalCopy.body = _m(
            'Since we just created levels, we wanted to honor your hard work. Congrats!'
          );
        }
      }
    }

    if (levelToShow.isMaxLevel()) {
      modalCopy.subtitle = _m("You've reached the max level!");
    }

    return modalCopy;
  }

  _isFTUE(): boolean {
    const levelToShow = this._levelToShow();
    return _.get(levelToShow, 'current_level') == 2 && _.get(levelToShow, 'last_seen_level') == 1;
  }

  _isGrandFathered(): boolean {
    const levelToShow = this._levelToShow();
    return _.get(levelToShow, 'current_level') > 2 && _.get(levelToShow, 'last_seen_level') == 1;
  }
}

export default new PlayerLevelUpController();
